import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import Icon from "Components/Icons/Icon";
import { ErrorToast } from "utils/toast";
import { InvoiceListFilterState } from "./InvoiceList";

interface InvoiceListFilterProps {
  filters: InvoiceListFilterState;
  show: boolean;
  onCloseClick: () => void;
  onFilterChange: (filters: {
    startDate: string | null;
    endDate: string | null;
    status: string | null;
  }) => void;
}

const STATUS_GROUPS = [["due"], ["overdue"], ["paid"]];

const InvoiceListFilter: React.FC<InvoiceListFilterProps> = ({
  filters,
  show,
  onCloseClick,
  onFilterChange,
}) => {
  console.log("filters", filters.status);
  const [startDate, setStartDate] = useState<string | null>(filters.startDate);
  const [endDate, setEndDate] = useState<string | null>(filters.endDate);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(
    filters.status
  );

  useEffect(() => {
    setStartDate(filters.startDate);
    setEndDate(filters.endDate);
    setSelectedStatus(filters.status);
  }, [filters.endDate, filters.startDate, filters.status]);

  const handleStatusChange = (status: string) => {
    setSelectedStatus((prevStatus) => (prevStatus === status ? null : status));
  };

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate && !endDate && !selectedStatus?.length) {
      ErrorToast("Please select at least one filter option.");
      return;
    }
    onFilterChange({ startDate, endDate, status: selectedStatus });
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedStatus(null);
    onFilterChange({ startDate: null, endDate: null, status: null });
  };

  const renderStatusGroup = (group: string[], rowIndex: number) => (
    <Row className="g-2 d-flex" key={rowIndex}>
      {group.map((status, colIndex) => (
        <Col key={colIndex}>
          <div className="form-check d-flex align-items-center gap-2">
            <Input
              className="form-check-input me-2"
              type="radio"
              checked={selectedStatus === status}
              onChange={() => handleStatusChange(status)}
              id={`${status}-${rowIndex}-${colIndex}`}
              style={{ width: 20, height: 20 }}
            />
            <Label
              className="text-black2D font-primary fw-normal fs-16 text-capitalize m-0"
              htmlFor={`${status}-${rowIndex}-${colIndex}`}
            >
              {status}
            </Label>
          </div>
        </Col>
      ))}
    </Row>
  );

  return (
    <Offcanvas direction="end" isOpen={show} toggle={onCloseClick}>
      <OffcanvasHeader className="bg-light bg-grayF5" toggle={onCloseClick}>
        <div className="text-black2D font-primary fw-medium fs-16">Filters</div>
      </OffcanvasHeader>

      <form className="d-flex flex-column justify-content-end h-100">
        <OffcanvasBody className="pt-3 mt-2">
          {/* DATE RANGE */}
          <Label className="text-grey89 font-primary fw-medium fs-14">
            DATE RANGE
          </Label>

          <div className="mb-4">
            <Row className="g-2 align-items-center">
              {/* START DATE */}
              <Col lg>
                <Label className="text-black2D font-primary fw-normal fs-14">
                  Start Date
                </Label>
                <div className="d-flex flex-row align-items-center">
                  <Flatpickr
                    value={startDate ? new Date(startDate) : undefined}
                    className="form-control br-7 fw-normal fs-16"
                    placeholder="Start Date"
                    options={{
                      maxDate: endDate ? new Date(endDate) : undefined,
                      onChange: (selectedDates, dateStr) => {
                        setStartDate(dateStr || null);
                      },
                    }}
                  />
                  <Icon
                    name="Calendar"
                    className="position-absolute"
                    style={{
                      right: 12,
                      cursor: "pointer",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </Col>

              {/* END DATE */}
              <Col lg>
                <Label className="text-black2D font-primary fw-normal fs-14">
                  End Date
                </Label>
                <div className="d-flex flex-row align-items-center">
                  <Flatpickr
                    value={endDate ? new Date(endDate) : undefined}
                    className="form-control br-7 fw-normal fs-16"
                    placeholder="End Date"
                    options={{
                      minDate: startDate ? new Date(startDate) : undefined,
                      onChange: (selectedDates, dateStr) => {
                        setEndDate(dateStr || null);
                      },
                    }}
                  />
                  <Icon
                    name="Calendar"
                    className="position-absolute"
                    style={{
                      right: 12,
                      cursor: "pointer",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </Col>
            </Row>
          </div>

          {/* STATUS */}
          <Label className="text-grey89 font-primary fw-medium fs-14">
            STATUS
          </Label>

          <Row className="g-2">
            {STATUS_GROUPS.map((group, index) =>
              renderStatusGroup(group, index)
            )}
          </Row>
        </OffcanvasBody>

        {/* FOOTER */}
        <div className="offcanvas-footer border-top p-3 text-center hstack gap-2">
          <button
            type="button"
            className="btn bg-greyF5 text-black2D fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleClear}
          >
            Clear
          </button>

          <button
            type="submit"
            className="btn bg-primary text-white fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default InvoiceListFilter;
